import * as THREE from 'three';
import { BlockId } from '../world/BlockType.js';
import { getPathDirection } from './Pathfinding.js';

// ============================================================
// HostileMobs.js - Zombie, Skeleton, Creeper, Spider, Enderman, Slime
// Hostile mob definitions, mesh builders, and AI
// ============================================================

// Minecraft pixel scale (same as EntityManager)
const PX = 1 / 16;

// -------------------- Utility --------------------

function simpleBox(w, h, d, color) {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
}

function emissiveBox(w, h, d, color) {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshBasicMaterial({ color })
  );
}

// ============================================================
// MOB TYPE STATS
// ============================================================

export const HOSTILE_MOB_TYPES = {
  ZOMBIE: {
    speed: 2.0,
    health: 20,
    damage: 2,
    attackRange: 1.8,
    attackCooldown: 1.0,
    detectionRange: 16,
    burnsInSunlight: true,
    sunDamagePerSec: 1,
    dropBlock: BlockId.DIRT,      // placeholder rotten flesh
    dropCount: 2,
    build: buildZombie,
  },
  SKELETON: {
    speed: 2.5,
    health: 20,
    damage: 3,
    attackRange: 30,              // ranged
    attackCooldown: 2.0,
    detectionRange: 16,
    preferredDistance: 8,
    maxDistance: 15,
    burnsInSunlight: true,
    sunDamagePerSec: 1,
    dropBlock: BlockId.COBBLESTONE, // placeholder bones
    dropCount: 2,
    build: buildSkeleton,
  },
  CREEPER: {
    speed: 2.0,
    health: 20,
    damage: 20,                   // max explosion damage
    detectionRange: 16,
    fuseRange: 3,
    fuseTime: 1.5,
    explosionRadius: 3,
    burnsInSunlight: false,
    dropBlock: BlockId.SAND,      // placeholder gunpowder
    dropCount: 2,
    build: buildCreeper,
  },
  SPIDER: {
    speed: 3.0,
    health: 16,
    damage: 2,
    attackRange: 2.0,
    attackCooldown: 1.2,
    detectionRange: 16,
    burnsInSunlight: false,
    hostileAtNight: true,         // neutral during day
    jumpForce: 8,
    dropBlock: BlockId.COBBLESTONE, // placeholder string
    dropCount: 2,
    build: buildSpider,
  },
  ENDERMAN: {
    speed: 3.5,
    health: 40,
    damage: 7,
    attackRange: 2,
    attackCooldown: 1.5,
    detectionRange: 64,
    burnsInSunlight: false,
    lookAggroThreshold: 0.98,     // dot product threshold for look-aggro
    teleportMinDist: 8,
    teleportMaxDist: 16,
    waterDamagePerSec: 1,
    build: buildEnderman,
  },
  SLIME: {
    speed: 1.5,
    health: 16,
    damage: 3,
    attackRange: 2,
    attackCooldown: 1.5,
    detectionRange: 16,
    burnsInSunlight: false,
    bounceInterval: 1.5,          // seconds between bounces
    bounceForce: 8,
    build: buildSlime,
  },
  // --- Nether mobs ---
  ZOMBIE_PIGLIN: {
    speed: 2.5,
    health: 20,
    damage: 5,
    attackRange: 2.0,
    attackCooldown: 1.0,
    detectionRange: 32,
    burnsInSunlight: false,
    neutral: true,                // Only attacks when provoked
    netherOnly: true,
    build: buildZombiePiglin,
  },
  GHAST: {
    speed: 1.5,
    health: 10,
    damage: 6,
    attackRange: 40,              // ranged: fireball
    attackCooldown: 3.0,
    detectionRange: 40,
    burnsInSunlight: false,
    floats: true,
    floatHeight: 15,              // hovers high
    netherOnly: true,
    build: buildGhast,
  },
  BLAZE: {
    speed: 2.0,
    health: 20,
    damage: 5,
    attackRange: 24,              // ranged: fireballs
    attackCooldown: 2.5,
    detectionRange: 24,
    burnsInSunlight: false,
    floats: true,
    floatHeight: 5,
    burstCount: 3,                // fires 3 fireballs in burst
    burstDelay: 0.3,              // delay between burst shots
    netherOnly: true,
    build: buildBlaze,
  },
  SHULKER: {
    speed: 0,                       // stationary
    health: 30,
    damage: 4,
    attackRange: 16,                // ranged: homing bullet
    attackCooldown: 3.5,
    detectionRange: 16,
    burnsInSunlight: false,
    floats: false,
    endOnly: true,
    build: buildShulker,
  },
  ENDERMITE: {
    speed: 3.5,
    health: 8,
    damage: 2,
    attackRange: 1.5,
    attackCooldown: 1.0,
    detectionRange: 12,
    burnsInSunlight: false,
    floats: false,
    endOnly: true,
    build: buildEndermite,
  },
  // --- Fase 5D mobs ---
  WITCH: {
    speed: 2.0,
    health: 26,
    damage: 0,
    attackRange: 10,
    attackCooldown: 3.0,
    detectionRange: 16,
    burnsInSunlight: false,
    swampOnly: true,
    build: buildWitch,
  },
  PHANTOM: {
    speed: 4.0,
    health: 20,
    damage: 4,
    attackRange: 2.0,
    attackCooldown: 2.0,
    detectionRange: 40,
    burnsInSunlight: true,
    sunDamagePerSec: 1,
    floats: true,
    floatHeight: 12,
    build: buildPhantom,
  },
  DROWNED: {
    speed: 1.5,
    health: 20,
    damage: 3,
    attackRange: 2.0,
    attackCooldown: 1.5,
    detectionRange: 16,
    burnsInSunlight: false,
    aquatic: true,
    build: buildDrowned,
  },
  PILLAGER: {
    speed: 2.5,
    health: 24,
    damage: 4,
    attackRange: 20,
    attackCooldown: 2.5,
    detectionRange: 16,
    burnsInSunlight: false,
    preferredDistance: 8,
    maxDistance: 15,
    build: buildPillager,
  },
  WARDEN: {
    speed: 1.8,
    health: 500,
    damage: 30,
    attackRange: 2.5,
    attackCooldown: 1.5,
    detectionRange: 20,
    burnsInSunlight: false,
    deepDarkOnly: true,
    build: buildWarden,
  },
};

// Sound intervals for hostile mobs
export const HOSTILE_SOUND_INTERVALS = {
  ZOMBIE:   [3, 8],
  SKELETON: [4, 10],
  CREEPER:  [999, 999], // silent until fuse
  SPIDER:   [5, 12],
  ENDERMAN: [4, 10],
  SLIME:    [2, 6],
  ZOMBIE_PIGLIN: [3, 8],
  GHAST:    [5, 15],
  BLAZE:    [3, 8],
  SHULKER:  [5, 12],
  ENDERMITE: [2, 6],
  WITCH:    [4, 10],
  PHANTOM:  [3, 8],
  DROWNED:  [3, 8],
  PILLAGER: [4, 10],
  WARDEN: [6, 15],
};

// ============================================================
// ZOMBIE MESH
// ============================================================

function buildZombie(group) {
  const S = PX * 1.8;

  const skinMain  = 0x4A7A2A;  // green skin
  const skinDark  = 0x3A6A1A;  // darker limbs
  const pantsCol  = 0x2A2A6A;  // tattered blue pants
  const shirtCol  = 0x3A6A2A;  // torn green shirt
  const eyeColor  = 0x222222;

  // Torso (8w x 12h x 4d)
  const torso = simpleBox(8 * S, 12 * S, 4 * S, shirtCol);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Tatters on shirt
  const tatter1 = simpleBox(3 * S, 2 * S, 0.5 * S, 0x2A5A1A);
  tatter1.position.set(2 * S, 13 * S, 2.3 * S);
  group.add(tatter1);
  const tatter2 = simpleBox(2 * S, 3 * S, 0.5 * S, 0x2A5A1A);
  tatter2.position.set(-3 * S, 14 * S, -2.3 * S);
  group.add(tatter2);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, skinMain);
  headGroup.add(head);

  // Eyes (dark, sunken)
  const eyeL = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Pupils (dark green glow)
  const pupilL = simpleBox(1 * S, 1 * S, 0.5 * S, 0x44AA44);
  pupilL.position.set(-1.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(1 * S, 1 * S, 0.5 * S, 0x44AA44);
  pupilR.position.set(2.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilR);

  // Mouth (groaning)
  const mouth = simpleBox(4 * S, 1 * S, 0.5 * S, 0x2A4A1A);
  mouth.position.set(0, -2 * S, 4.2 * S);
  headGroup.add(mouth);

  // Nose
  const nose = simpleBox(1.5 * S, 1.5 * S, 1 * S, skinDark);
  nose.position.set(0, 0, 4.5 * S);
  headGroup.add(nose);

  // Hair (messy/sparse)
  const hairTop = simpleBox(8.2 * S, 1 * S, 6 * S, 0x2A3A1A);
  hairTop.position.set(0, 4.5 * S, -1 * S);
  headGroup.add(hairTop);

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d) - stretched forward like classic zombie
  const armL = new THREE.Group();
  const armLupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  const handL = simpleBox(4 * S, 2 * S, 4 * S, skinDark);
  handL.position.set(0, -13 * S, 0);
  armL.add(handL);
  armL.position.set(-6 * S, 24 * S, 0);
  armL.rotation.x = -1.2; // Arms stretched forward
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);
  const handR = simpleBox(4 * S, 2 * S, 4 * S, skinDark);
  handR.position.set(0, -13 * S, 0);
  armR.add(handR);
  armR.position.set(6 * S, 24 * S, 0);
  armR.rotation.x = -1.2; // Arms stretched forward
  group.add(armR);

  // Legs (4w x 12h x 4d)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legLmesh.position.y = -6 * S;
  legFL.add(legLmesh);
  const shoeL = simpleBox(4.2 * S, 2 * S, 4.5 * S, 0x333333);
  shoeL.position.set(0, -12.5 * S, 0.2 * S);
  legFL.add(shoeL);
  legFL.position.set(-2 * S, 12 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legRmesh.position.y = -6 * S;
  legFR.add(legRmesh);
  const shoeR = simpleBox(4.2 * S, 2 * S, 4.5 * S, 0x333333);
  shoeR.position.set(0, -12.5 * S, 0.2 * S);
  legFR.add(shoeR);
  legFR.position.set(2 * S, 12 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// SKELETON MESH
// ============================================================

function buildSkeleton(group) {
  const S = PX * 1.8;

  const boneMain = 0xE8E8D8; // white/bone
  const boneDark = 0xD8D8C8; // slightly darker
  const eyeColor = 0x111111; // dark eye sockets

  // Torso - thinner than normal (6w x 12h x 3d)
  const torso = simpleBox(6 * S, 12 * S, 3 * S, boneMain);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Ribs detail
  const rib1 = simpleBox(7 * S, 0.8 * S, 0.5 * S, boneDark);
  rib1.position.set(0, 22 * S, 1.8 * S);
  group.add(rib1);
  const rib2 = simpleBox(7 * S, 0.8 * S, 0.5 * S, boneDark);
  rib2.position.set(0, 20 * S, 1.8 * S);
  group.add(rib2);
  const rib3 = simpleBox(7 * S, 0.8 * S, 0.5 * S, boneDark);
  rib3.position.set(0, 18 * S, 1.8 * S);
  group.add(rib3);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, boneMain);
  headGroup.add(head);

  // Eye sockets (dark recesses)
  const eyeSocketL = simpleBox(2.5 * S, 2.5 * S, 1 * S, 0x111111);
  eyeSocketL.position.set(-2 * S, 1 * S, 3.8 * S);
  headGroup.add(eyeSocketL);
  const eyeSocketR = simpleBox(2.5 * S, 2.5 * S, 1 * S, 0x111111);
  eyeSocketR.position.set(2 * S, 1 * S, 3.8 * S);
  headGroup.add(eyeSocketR);

  // Glowing red pupils inside sockets
  const pupilL = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xFF2222);
  pupilL.position.set(-2 * S, 1 * S, 4.3 * S);
  headGroup.add(pupilL);
  const pupilR = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xFF2222);
  pupilR.position.set(2 * S, 1 * S, 4.3 * S);
  headGroup.add(pupilR);

  // Nose hole
  const noseHole = simpleBox(1.5 * S, 1.5 * S, 0.5 * S, 0x333333);
  noseHole.position.set(0, -0.5 * S, 4.2 * S);
  headGroup.add(noseHole);

  // Jaw/teeth
  const jaw = simpleBox(6 * S, 1 * S, 0.5 * S, boneDark);
  jaw.position.set(0, -3 * S, 4.2 * S);
  headGroup.add(jaw);
  // Teeth
  for (let i = -2; i <= 2; i++) {
    const tooth = simpleBox(0.8 * S, 1 * S, 0.5 * S, 0xFFFFFF);
    tooth.position.set(i * 1.2 * S, -2.2 * S, 4.2 * S);
    headGroup.add(tooth);
  }

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (3w x 12h x 3d) - thinner, bony
  const armL = new THREE.Group();
  const armLupper = simpleBox(3 * S, 12 * S, 3 * S, boneDark);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  const handL = simpleBox(3 * S, 2 * S, 3 * S, boneDark);
  handL.position.set(0, -13 * S, 0);
  armL.add(handL);
  armL.position.set(-5.5 * S, 24 * S, 0);
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(3 * S, 12 * S, 3 * S, boneDark);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);
  const handR = simpleBox(3 * S, 2 * S, 3 * S, boneDark);
  handR.position.set(0, -13 * S, 0);
  armR.add(handR);
  armR.position.set(5.5 * S, 24 * S, 0);
  group.add(armR);

  // Bow in left hand (thin dark rectangle + string)
  const bowGroup = new THREE.Group();
  const bowBody = simpleBox(0.5 * S, 10 * S, 1 * S, 0x5A3A1A);
  bowGroup.add(bowBody);
  // Bow curve (two angled pieces)
  const bowTop = simpleBox(0.5 * S, 4 * S, 0.8 * S, 0x5A3A1A);
  bowTop.position.set(0, 6 * S, 0.5 * S);
  bowTop.rotation.x = 0.3;
  bowGroup.add(bowTop);
  const bowBot = simpleBox(0.5 * S, 4 * S, 0.8 * S, 0x5A3A1A);
  bowBot.position.set(0, -6 * S, 0.5 * S);
  bowBot.rotation.x = -0.3;
  bowGroup.add(bowBot);
  // String
  const bowString = simpleBox(0.2 * S, 12 * S, 0.2 * S, 0xCCCCCC);
  bowString.position.set(0, 0, 1.5 * S);
  bowGroup.add(bowString);
  bowGroup.position.set(0, -10 * S, 2 * S);
  armL.add(bowGroup);

  // Legs (3w x 12h x 3d) - thinner
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(3 * S, 12 * S, 3 * S, boneDark);
  legLmesh.position.y = -6 * S;
  legFL.add(legLmesh);
  const footL = simpleBox(3.2 * S, 1.5 * S, 4 * S, boneMain);
  footL.position.set(0, -12.5 * S, 0.3 * S);
  legFL.add(footL);
  legFL.position.set(-2 * S, 12 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(3 * S, 12 * S, 3 * S, boneDark);
  legRmesh.position.y = -6 * S;
  legFR.add(legRmesh);
  const footR = simpleBox(3.2 * S, 1.5 * S, 4 * S, boneMain);
  footR.position.set(0, -12.5 * S, 0.3 * S);
  legFR.add(footR);
  legFR.position.set(2 * S, 12 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// CREEPER MESH
// ============================================================

function buildCreeper(group) {
  const S = PX * 1.8;

  const bodyGreen  = 0x2A8A2A;
  const bodyDark   = 0x1A6A1A;
  const faceBlack  = 0x111111;

  // Body (8w x 12h x 4d) - no arms on a creeper
  const body = simpleBox(8 * S, 12 * S, 4 * S, bodyGreen);
  body.position.set(0, 14 * S, 0);
  group.add(body);

  // Mottled texture spots on body
  const spot1 = simpleBox(3 * S, 3 * S, 0.3 * S, bodyDark);
  spot1.position.set(2 * S, 17 * S, 2.2 * S);
  group.add(spot1);
  const spot2 = simpleBox(2 * S, 2 * S, 0.3 * S, bodyDark);
  spot2.position.set(-2 * S, 13 * S, 2.2 * S);
  group.add(spot2);
  const spot3 = simpleBox(3 * S, 2 * S, 0.3 * S, bodyDark);
  spot3.position.set(0, 18 * S, -2.2 * S);
  group.add(spot3);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, bodyGreen);
  headGroup.add(head);

  // Spots on head
  const headSpot1 = simpleBox(2 * S, 2 * S, 0.3 * S, bodyDark);
  headSpot1.position.set(3 * S, 2 * S, 4.2 * S);
  headGroup.add(headSpot1);

  // ---- Iconic creeper face ----
  // Eyes (two dark squares, angled inward for sad/angry look)
  const eyeL = simpleBox(2 * S, 2 * S, 0.5 * S, faceBlack);
  eyeL.position.set(-2 * S, 2 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 2 * S, 0.5 * S, faceBlack);
  eyeR.position.set(2 * S, 2 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Frown / mouth - the iconic creeper face:
  // Vertical middle line
  const mouthV = simpleBox(2 * S, 3 * S, 0.5 * S, faceBlack);
  mouthV.position.set(0, -2 * S, 4.2 * S);
  headGroup.add(mouthV);
  // Horizontal top
  const mouthH = simpleBox(4 * S, 1 * S, 0.5 * S, faceBlack);
  mouthH.position.set(0, -0.5 * S, 4.2 * S);
  headGroup.add(mouthH);
  // Down extensions on sides
  const mouthLL = simpleBox(1 * S, 2 * S, 0.5 * S, faceBlack);
  mouthLL.position.set(-2 * S, -2 * S, 4.2 * S);
  headGroup.add(mouthLL);
  const mouthRR = simpleBox(1 * S, 2 * S, 0.5 * S, faceBlack);
  mouthRR.position.set(2 * S, -2 * S, 4.2 * S);
  headGroup.add(mouthRR);

  headGroup.position.set(0, 24 * S, 0);
  group.add(headGroup);

  // NO ARMS - creeper has no arms

  // 4 short legs (4w x 6h x 4d)
  const legH = 6 * S;
  const legW = 4 * S;

  const legFL = new THREE.Group();
  const legFLmesh = simpleBox(legW, legH, legW, bodyGreen);
  legFLmesh.position.y = -legH / 2;
  legFL.add(legFLmesh);
  legFL.position.set(-2 * S, 8 * S, 2 * S);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legFRmesh = simpleBox(legW, legH, legW, bodyGreen);
  legFRmesh.position.y = -legH / 2;
  legFR.add(legFRmesh);
  legFR.position.set(2 * S, 8 * S, 2 * S);
  group.add(legFR);

  const legBL = new THREE.Group();
  const legBLmesh = simpleBox(legW, legH, legW, bodyGreen);
  legBLmesh.position.y = -legH / 2;
  legBL.add(legBLmesh);
  legBL.position.set(-2 * S, 8 * S, -2 * S);
  group.add(legBL);

  const legBR = new THREE.Group();
  const legBRmesh = simpleBox(legW, legH, legW, bodyGreen);
  legBRmesh.position.y = -legH / 2;
  legBR.add(legBRmesh);
  legBR.position.set(2 * S, 8 * S, -2 * S);
  group.add(legBR);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR, armL: null, armR: null };
}

// ============================================================
// SPIDER MESH
// ============================================================

function buildSpider(group) {
  const S = PX * 1.8;

  const bodyDark = 0x3A2A1A;
  const bodyMid  = 0x4A3A2A;
  const eyeRed   = 0xFF0000;

  // Abdomen (rear body, larger: 8w x 6h x 10d)
  const abdomen = simpleBox(10 * S, 6 * S, 10 * S, bodyDark);
  abdomen.position.set(0, 5 * S, -4 * S);
  group.add(abdomen);

  // Thorax (front body, 6w x 5h x 6d)
  const thorax = simpleBox(6 * S, 5 * S, 6 * S, bodyMid);
  thorax.position.set(0, 5 * S, 4 * S);
  group.add(thorax);

  // Head (small, 4w x 4h x 4d)
  const headGroup = new THREE.Group();
  const head = simpleBox(5 * S, 4 * S, 4 * S, bodyMid);
  headGroup.add(head);

  // 8 red eyes (2 rows of 4)
  const eyePositions = [
    [-1.5, 1, 2.2], [1.5, 1, 2.2],    // top row outer
    [-0.5, 1, 2.2], [0.5, 1, 2.2],     // top row inner
    [-1.2, 0, 2.2], [1.2, 0, 2.2],     // bottom row outer
    [-0.4, 0, 2.2], [0.4, 0, 2.2],     // bottom row inner
  ];
  for (const [ex, ey, ez] of eyePositions) {
    const eye = emissiveBox(0.6 * S, 0.6 * S, 0.4 * S, eyeRed);
    eye.position.set(ex * S, ey * S, ez * S);
    headGroup.add(eye);
  }

  // Mandibles
  const mandL = simpleBox(0.5 * S, 1.5 * S, 1 * S, 0x222222);
  mandL.position.set(-1 * S, -1.5 * S, 2 * S);
  mandL.rotation.x = 0.3;
  headGroup.add(mandL);
  const mandR = simpleBox(0.5 * S, 1.5 * S, 1 * S, 0x222222);
  mandR.position.set(1 * S, -1.5 * S, 2 * S);
  mandR.rotation.x = 0.3;
  headGroup.add(mandR);

  headGroup.position.set(0, 6 * S, 8 * S);
  group.add(headGroup);

  // 8 Legs (4 per side), thin and angular
  const legLength = 8 * S;
  const legThick = 0.8 * S;
  const legs = [];

  // Leg attachment Z offsets (along body): front to back
  const legZoffsets = [3, 0, -3, -6];
  const legAngles   = [0.6, 0.3, -0.3, -0.6]; // spread angles

  for (let side = -1; side <= 1; side += 2) { // -1 = left, 1 = right
    for (let li = 0; li < 4; li++) {
      const legGroup = new THREE.Group();

      // Upper leg segment
      const upper = simpleBox(legThick, legLength, legThick, bodyDark);
      upper.position.set(side * legLength * 0.4, 0, 0);
      upper.rotation.z = side * 1.0;  // spread outward
      upper.rotation.y = legAngles[li];
      legGroup.add(upper);

      // Lower leg segment (angled down)
      const lower = simpleBox(legThick, legLength * 0.7, legThick, bodyMid);
      lower.position.set(side * legLength * 0.85, -legLength * 0.4, 0);
      lower.rotation.z = side * 0.3;
      lower.rotation.y = legAngles[li];
      legGroup.add(lower);

      legGroup.position.set(side * 3 * S, 5 * S, legZoffsets[li] * S);
      group.add(legGroup);
      legs.push(legGroup);
    }
  }

  // Store legs for animation (left 4, then right 4)
  // We use legFL/legFR for the front pair and legBL/legBR for a back pair for basic animation
  return {
    headMesh: headGroup,
    legFL: legs[0],  // left front
    legFR: legs[4],  // right front
    legBL: legs[3],  // left back
    legBR: legs[7],  // right back
    armL: null,
    armR: null,
    allLegs: legs,   // all 8 legs for advanced animation
  };
}

// ============================================================
// ENDERMAN MESH
// ============================================================

function buildEnderman(group) {
  const S = PX * 1.8;

  const bodyBlack = 0x111111;
  const bodyDark  = 0x0A0A0A;
  const eyePurple = 0xCC44FF;

  // Torso (6w x 14h x 3d) - thinner, taller than most mobs
  const torso = simpleBox(6 * S, 14 * S, 3 * S, bodyBlack);
  torso.position.set(0, 24 * S, 0);
  group.add(torso);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, bodyBlack);
  headGroup.add(head);

  // Purple eyes (emissive glow)
  const eyeL = emissiveBox(2 * S, 1 * S, 0.5 * S, eyePurple);
  eyeL.position.set(-2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = emissiveBox(2 * S, 1 * S, 0.5 * S, eyePurple);
  eyeR.position.set(2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Subtle purple glow effect around eyes
  const glowL = emissiveBox(2.5 * S, 1.5 * S, 0.3 * S, 0x8822AA);
  glowL.position.set(-2 * S, 1 * S, 4.4 * S);
  headGroup.add(glowL);
  const glowR = emissiveBox(2.5 * S, 1.5 * S, 0.3 * S, 0x8822AA);
  glowR.position.set(2 * S, 1 * S, 4.4 * S);
  headGroup.add(glowR);

  // Jaw (slightly open, menacing)
  const jaw = simpleBox(6 * S, 1 * S, 0.5 * S, bodyDark);
  jaw.position.set(0, -3 * S, 4.2 * S);
  headGroup.add(jaw);

  headGroup.position.set(0, 35 * S, 0);
  group.add(headGroup);

  // Arms (2w x 18h x 2d) - long and thin, Enderman signature
  const armL = new THREE.Group();
  const armLupper = simpleBox(2 * S, 18 * S, 2 * S, bodyDark);
  armLupper.position.y = -9 * S;
  armL.add(armLupper);
  const handL = simpleBox(2 * S, 2 * S, 2 * S, bodyDark);
  handL.position.set(0, -19 * S, 0);
  armL.add(handL);
  armL.position.set(-4 * S, 31 * S, 0);
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(2 * S, 18 * S, 2 * S, bodyDark);
  armRupper.position.y = -9 * S;
  armR.add(armRupper);
  const handR = simpleBox(2 * S, 2 * S, 2 * S, bodyDark);
  handR.position.set(0, -19 * S, 0);
  armR.add(handR);
  armR.position.set(4 * S, 31 * S, 0);
  group.add(armR);

  // Legs (2w x 18h x 2d) - long thin legs
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(2 * S, 18 * S, 2 * S, bodyDark);
  legLmesh.position.y = -9 * S;
  legFL.add(legLmesh);
  const footL = simpleBox(2.5 * S, 1.5 * S, 3 * S, bodyBlack);
  footL.position.set(0, -18.5 * S, 0.3 * S);
  legFL.add(footL);
  legFL.position.set(-2 * S, 17 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(2 * S, 18 * S, 2 * S, bodyDark);
  legRmesh.position.y = -9 * S;
  legFR.add(legRmesh);
  const footR = simpleBox(2.5 * S, 1.5 * S, 3 * S, bodyBlack);
  footR.position.set(0, -18.5 * S, 0.3 * S);
  legFR.add(footR);
  legFR.position.set(2 * S, 17 * S, 0);
  group.add(legFR);

  // Purple particles effect (small emissive cubes around body)
  for (let i = 0; i < 4; i++) {
    const particle = emissiveBox(0.5 * S, 0.5 * S, 0.5 * S, 0xAA44DD);
    particle.position.set(
      (Math.random() - 0.5) * 8 * S,
      (5 + Math.random() * 30) * S,
      (Math.random() - 0.5) * 6 * S
    );
    group.add(particle);
  }

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// SLIME MESH
// ============================================================

function buildSlime(group, scaleMultiplier) {
  const S = PX * 1.8;
  const scale = scaleMultiplier || 1.0;

  // Outer body - semi-transparent green cube
  const outerSize = 12 * S * scale;
  const outerGeo = new THREE.BoxGeometry(outerSize, outerSize, outerSize);
  const outerMat = new THREE.MeshLambertMaterial({
    color: 0x66CC44,
    transparent: true,
    opacity: 0.5,
  });
  const outer = new THREE.Mesh(outerGeo, outerMat);
  outer.position.set(0, outerSize / 2, 0);
  group.add(outer);

  // Inner core - smaller, opaque green cube
  const innerSize = 6 * S * scale;
  const inner = simpleBox(innerSize, innerSize, innerSize, 0x448822);
  inner.position.set(0, outerSize / 2, 0);
  group.add(inner);

  // Eyes on front face
  const eyeSize = 1.5 * S * scale;
  const eyeZ = outerSize / 2 + 0.2 * S;
  const eyeY = outerSize / 2 + 1 * S * scale;

  const eyeL = simpleBox(eyeSize, eyeSize * 1.5, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S * scale, eyeY, eyeZ);
  group.add(eyeL);
  const eyeR = simpleBox(eyeSize, eyeSize * 1.5, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S * scale, eyeY, eyeZ);
  group.add(eyeR);

  // Mouth (simple line)
  const mouth = simpleBox(3 * S * scale, 0.5 * S, 0.5 * S, 0x111111);
  mouth.position.set(0, outerSize / 2 - 2 * S * scale, eyeZ);
  group.add(mouth);

  // No limbs -- slimes are just cubes
  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// ZOMBIE PIGLIN MESH
// ============================================================

function buildZombiePiglin(group) {
  const S = PX * 1.8;

  const skinPink   = 0xD4A080;  // piglin skin
  const skinDark   = 0xB08060;  // darker areas
  const goldColor  = 0xFFD700;  // gold details
  const greenRot   = 0x6A8A4A;  // zombie rot patches
  const pantsCol   = 0x5A3A1A;  // brown pants

  // Torso (8w x 12h x 4d)
  const torso = simpleBox(8 * S, 12 * S, 4 * S, skinPink);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Gold chestplate detail
  const goldChest = simpleBox(8.4 * S, 6 * S, 4.4 * S, goldColor);
  goldChest.position.set(0, 20 * S, 0);
  group.add(goldChest);

  // Rot patches on skin
  const rot1 = simpleBox(3 * S, 2 * S, 0.5 * S, greenRot);
  rot1.position.set(2 * S, 15 * S, 2.3 * S);
  group.add(rot1);
  const rot2 = simpleBox(2 * S, 3 * S, 0.5 * S, greenRot);
  rot2.position.set(-3 * S, 16 * S, -2.3 * S);
  group.add(rot2);

  // Head (8x8x8) - pig-like snout
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, skinPink);
  headGroup.add(head);

  // Snout (pig nose protruding)
  const snout = simpleBox(4 * S, 3 * S, 3 * S, skinDark);
  snout.position.set(0, -1 * S, 5.5 * S);
  headGroup.add(snout);

  // Nostrils
  const nostrilL = simpleBox(1 * S, 1 * S, 0.5 * S, 0x8A5A3A);
  nostrilL.position.set(-1 * S, -1 * S, 7.2 * S);
  headGroup.add(nostrilL);
  const nostrilR = simpleBox(1 * S, 1 * S, 0.5 * S, 0x8A5A3A);
  nostrilR.position.set(1 * S, -1 * S, 7.2 * S);
  headGroup.add(nostrilR);

  // Eyes (dark, zombie-like)
  const eyeL = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S, 1.5 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S, 1.5 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Red glow pupils
  const pupilL = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xFF4444);
  pupilL.position.set(-2 * S, 1.5 * S, 4.5 * S);
  headGroup.add(pupilL);
  const pupilR = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xFF4444);
  pupilR.position.set(2 * S, 1.5 * S, 4.5 * S);
  headGroup.add(pupilR);

  // Ears (pig-like, floppy)
  const earL = simpleBox(1.5 * S, 3 * S, 2 * S, skinDark);
  earL.position.set(-4.5 * S, 3 * S, 0);
  earL.rotation.z = 0.3;
  headGroup.add(earL);
  const earR = simpleBox(1.5 * S, 3 * S, 2 * S, skinDark);
  earR.position.set(4.5 * S, 3 * S, 0);
  earR.rotation.z = -0.3;
  headGroup.add(earR);

  // Gold crown/band on head
  const goldBand = simpleBox(8.4 * S, 2 * S, 8.4 * S, goldColor);
  goldBand.position.set(0, 3 * S, 0);
  headGroup.add(goldBand);

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d)
  const armL = new THREE.Group();
  const armLupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  armL.position.set(-6 * S, 24 * S, 0);
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);

  // Gold sword in right hand
  const sword = simpleBox(0.8 * S, 10 * S, 0.8 * S, goldColor);
  sword.position.set(0, -14 * S, 2 * S);
  armR.add(sword);

  armR.position.set(6 * S, 24 * S, 0);
  group.add(armR);

  // Legs (4w x 12h x 4d)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legLmesh.position.y = -6 * S;
  legFL.add(legLmesh);
  legFL.position.set(-2 * S, 12 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legRmesh.position.y = -6 * S;
  legFR.add(legRmesh);
  legFR.position.set(2 * S, 12 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// GHAST MESH
// ============================================================

function buildGhast(group) {
  const S = PX * 1.8;

  const bodyWhite = 0xF0F0F0;
  const bodyGray  = 0xD8D8D8;

  // Large cubic body (4x4x4 blocks = ~32px each side)
  const bodySize = 24 * S;
  const body = simpleBox(bodySize, bodySize, bodySize, bodyWhite);
  body.position.set(0, bodySize / 2 + 4 * S, 0);
  group.add(body);

  // Slightly darker patches
  const patch1 = simpleBox(8 * S, 8 * S, 0.5 * S, bodyGray);
  patch1.position.set(4 * S, bodySize / 2 + 8 * S, bodySize / 2 + 0.3 * S);
  group.add(patch1);
  const patch2 = simpleBox(6 * S, 6 * S, 0.5 * S, bodyGray);
  patch2.position.set(-5 * S, bodySize / 2 + 2 * S, bodySize / 2 + 0.3 * S);
  group.add(patch2);

  // Sad face
  // Eyes (dark, teardrop-shaped)
  const eyeL = simpleBox(3 * S, 4 * S, 1 * S, 0x222222);
  eyeL.position.set(-5 * S, bodySize / 2 + 8 * S, bodySize / 2 + 0.5 * S);
  group.add(eyeL);
  const eyeR = simpleBox(3 * S, 4 * S, 1 * S, 0x222222);
  eyeR.position.set(5 * S, bodySize / 2 + 8 * S, bodySize / 2 + 0.5 * S);
  group.add(eyeR);

  // Red pupils
  const pupilL = emissiveBox(1.5 * S, 2 * S, 0.5 * S, 0xFF4444);
  pupilL.position.set(-5 * S, bodySize / 2 + 8 * S, bodySize / 2 + 1 * S);
  group.add(pupilL);
  const pupilR = emissiveBox(1.5 * S, 2 * S, 0.5 * S, 0xFF4444);
  pupilR.position.set(5 * S, bodySize / 2 + 8 * S, bodySize / 2 + 1 * S);
  group.add(pupilR);

  // Sad mouth (downward curve)
  const mouthL = simpleBox(3 * S, 1 * S, 0.5 * S, 0x333333);
  mouthL.position.set(-3 * S, bodySize / 2 + 1 * S, bodySize / 2 + 0.5 * S);
  group.add(mouthL);
  const mouthR = simpleBox(3 * S, 1 * S, 0.5 * S, 0x333333);
  mouthR.position.set(3 * S, bodySize / 2 + 1 * S, bodySize / 2 + 0.5 * S);
  group.add(mouthR);
  const mouthCenter = simpleBox(2 * S, 1 * S, 0.5 * S, 0x333333);
  mouthCenter.position.set(0, bodySize / 2 + 0 * S, bodySize / 2 + 0.5 * S);
  group.add(mouthCenter);

  // Tentacles hanging down (9 tentacles in a 3x3 grid)
  for (let tx = -1; tx <= 1; tx++) {
    for (let tz = -1; tz <= 1; tz++) {
      const tentLen = 10 + Math.floor(Math.abs(tx + tz) * 3);
      const tentGroup = new THREE.Group();
      for (let i = 0; i < tentLen; i++) {
        const seg = simpleBox(2 * S, 2 * S, 2 * S, i % 2 === 0 ? bodyWhite : bodyGray);
        seg.position.set(0, -i * 2 * S, 0);
        tentGroup.add(seg);
      }
      tentGroup.position.set(tx * 6 * S, 4 * S, tz * 6 * S);
      group.add(tentGroup);
    }
  }

  // No limbs
  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// BLAZE MESH
// ============================================================

function buildBlaze(group) {
  const S = PX * 1.8;

  const blazeYellow = 0xFFAA22;
  const blazeOrange = 0xFF6600;
  const blazeDark   = 0xDD4400;

  // Central head/body (8x8x8) - glowing
  const headGroup = new THREE.Group();
  const head = emissiveBox(8 * S, 8 * S, 8 * S, blazeYellow);
  headGroup.add(head);

  // Eyes (dark)
  const eyeL = simpleBox(2 * S, 1 * S, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 1 * S, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Glowing pupils (orange)
  const pupilL = emissiveBox(1 * S, 0.5 * S, 0.3 * S, 0xFFFF00);
  pupilL.position.set(-2 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilL);
  const pupilR = emissiveBox(1 * S, 0.5 * S, 0.3 * S, 0xFFFF00);
  pupilR.position.set(2 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilR);

  headGroup.position.set(0, 16 * S, 0);
  group.add(headGroup);

  // Spinning rod segments around body (12 rods in 3 layers)
  const rodMaterial = new THREE.MeshBasicMaterial({ color: blazeOrange });
  for (let layer = 0; layer < 3; layer++) {
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + layer * 0.5;
      const rodLen = 8 * S;
      const rodThick = 1.5 * S;
      const rodGeo = new THREE.BoxGeometry(rodThick, rodLen, rodThick);
      const rod = new THREE.Mesh(rodGeo, rodMaterial);

      const radius = 6 * S;
      const yOff = (layer - 1) * 6 * S + 16 * S;
      rod.position.set(
        Math.cos(angle) * radius,
        yOff,
        Math.sin(angle) * radius
      );
      rod.rotation.z = 0.2;
      rod.rotation.x = 0.1;
      group.add(rod);
    }
  }

  // Core smoke/fire particles (emissive cubes below body)
  for (let i = 0; i < 4; i++) {
    const particle = emissiveBox(1 * S, 1 * S, 1 * S, 0xFF8800);
    particle.position.set(
      (Math.random() - 0.5) * 6 * S,
      (6 + Math.random() * 8) * S,
      (Math.random() - 0.5) * 6 * S
    );
    group.add(particle);
  }

  // No legs/arms (blaze floats)
  return { headMesh: headGroup, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// SHULKER MESH (End mob — stationary box)
// ============================================================

function buildShulker(group) {
  const S = PX * 1.8;

  // Main body (purple box)
  const body = simpleBox(16 * S, 16 * S, 16 * S, 0x9B59B6);
  body.position.y = 8 * S;
  group.add(body);

  // Lid (lighter purple, top part)
  const lid = simpleBox(16 * S, 6 * S, 16 * S, 0xB070CC);
  lid.position.y = 19 * S;
  group.add(lid);

  // Inner "face" visible when open
  const face = simpleBox(10 * S, 8 * S, 10 * S, 0x6B4483);
  face.position.y = 12 * S;
  group.add(face);

  // Eyes (small purple glowing)
  const eyeL = emissiveBox(2 * S, 2 * S, 1 * S, 0xE74C8B);
  eyeL.position.set(-3 * S, 14 * S, 8 * S);
  group.add(eyeL);
  const eyeR = emissiveBox(2 * S, 2 * S, 1 * S, 0xE74C8B);
  eyeR.position.set(3 * S, 14 * S, 8 * S);
  group.add(eyeR);

  return { headMesh: lid, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// ENDERMITE MESH (tiny End mob)
// ============================================================

function buildEndermite(group) {
  const S = PX * 1.2;

  // Segmented body (3 segments)
  const colors = [0x7D3C98, 0x6C3483, 0x5B2C6F];
  for (let i = 0; i < 3; i++) {
    const seg = simpleBox(4 * S, 3 * S, 5 * S, colors[i]);
    seg.position.set(0, 1.5 * S, (i - 1) * 5 * S);
    group.add(seg);
  }

  // Tiny legs (6 legs)
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < 3; i++) {
      const leg = simpleBox(1 * S, 2 * S, 1 * S, 0x4A1A6B);
      leg.position.set(side * 3 * S, 0, (i - 1) * 4 * S);
      group.add(leg);
    }
  }

  // Eyes
  const eyeL = emissiveBox(1 * S, 1 * S, 1 * S, 0xB070CC);
  eyeL.position.set(-1.5 * S, 3 * S, -5 * S);
  group.add(eyeL);
  const eyeR = emissiveBox(1 * S, 1 * S, 1 * S, 0xB070CC);
  eyeR.position.set(1.5 * S, 3 * S, -5 * S);
  group.add(eyeR);

  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// WITCH MESH (Fase 5D — swamp caster)
// ============================================================

function buildWitch(group) {
  const S = PX * 1.8;

  const robeTop    = 0x6A1B9A;  // purple robe
  const robeBottom = 0x4A148C;  // darker robe hem
  const skinGreen  = 0x5A8A3A;  // green witch skin
  const hatColor   = 0x2E0854;  // dark purple hat

  // Torso / robe upper (8w x 12h x 4d)
  const torso = simpleBox(8 * S, 12 * S, 4 * S, robeTop);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Robe bottom / skirt (10w x 6h x 6d) — wider than torso
  const robeBot = simpleBox(10 * S, 6 * S, 6 * S, robeBottom);
  robeBot.position.set(0, 9 * S, 0);
  group.add(robeBot);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, skinGreen);
  headGroup.add(head);

  // Big nose (protruding)
  const nose = simpleBox(2 * S, 3 * S, 3 * S, 0x4A7A2A);
  nose.position.set(0, 0, 5.5 * S);
  headGroup.add(nose);

  // Wart on nose
  const wart = simpleBox(1 * S, 1 * S, 1 * S, 0x3A6A1A);
  wart.position.set(0.5 * S, 1 * S, 7 * S);
  headGroup.add(wart);

  // Red eyes
  const eyeL = emissiveBox(1.5 * S, 1.5 * S, 0.5 * S, 0xFF2222);
  eyeL.position.set(-2 * S, 1.5 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = emissiveBox(1.5 * S, 1.5 * S, 0.5 * S, 0xFF2222);
  eyeR.position.set(2 * S, 1.5 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Mouth (thin line)
  const mouth = simpleBox(4 * S, 0.5 * S, 0.5 * S, 0x333333);
  mouth.position.set(0, -2.5 * S, 4.2 * S);
  headGroup.add(mouth);

  // Pointy hat — 3 stacked boxes getting smaller
  const hat1 = simpleBox(8 * S, 4 * S, 8 * S, hatColor);
  hat1.position.set(0, 6 * S, 0);
  headGroup.add(hat1);
  const hat2 = simpleBox(6 * S, 4 * S, 6 * S, hatColor);
  hat2.position.set(0, 10 * S, 0);
  headGroup.add(hat2);
  const hat3 = simpleBox(4 * S, 6 * S, 4 * S, hatColor);
  hat3.position.set(0, 15 * S, 0);
  headGroup.add(hat3);

  // Hat brim
  const brim = simpleBox(12 * S, 1 * S, 12 * S, hatColor);
  brim.position.set(0, 4.5 * S, 0);
  headGroup.add(brim);

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d) — forward, holding potion
  const armL = new THREE.Group();
  const armLupper = simpleBox(4 * S, 12 * S, 4 * S, robeTop);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  armL.position.set(-6 * S, 24 * S, 0);
  armL.rotation.x = -0.8;
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4 * S, 12 * S, 4 * S, robeTop);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);

  // Potion bottle in right hand (emissive green)
  const potion = emissiveBox(3 * S, 4 * S, 3 * S, 0x4CAF50);
  potion.position.set(0, -14 * S, 0);
  armR.add(potion);

  armR.position.set(6 * S, 24 * S, 0);
  armR.rotation.x = -0.8;
  group.add(armR);

  // Legs (hidden mostly by robe, but still present for animation)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4 * S, 10 * S, 4 * S, robeBottom);
  legLmesh.position.y = -5 * S;
  legFL.add(legLmesh);
  legFL.position.set(-2 * S, 6 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4 * S, 10 * S, 4 * S, robeBottom);
  legRmesh.position.y = -5 * S;
  legFR.add(legRmesh);
  legFR.position.set(2 * S, 6 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// PHANTOM MESH (Fase 5D — flying undead)
// ============================================================

function buildPhantom(group) {
  const S = PX * 1.8;

  const bodyTeal = 0x1E6B6B;
  const wingDark = 0x165555;
  const eyeGreen = 0x44FF44;

  // Flat manta-ray body (20w x 3h x 12d)
  const body = simpleBox(20 * S, 3 * S, 12 * S, bodyTeal);
  body.position.set(0, 2 * S, 0);
  group.add(body);

  // Head (4w x 3h x 4d) at front
  const headGroup = new THREE.Group();
  const head = simpleBox(4 * S, 3 * S, 4 * S, bodyTeal);
  headGroup.add(head);

  // Glowing green eyes (emissive)
  const eyeL = emissiveBox(1 * S, 1 * S, 0.5 * S, eyeGreen);
  eyeL.position.set(-1.2 * S, 0.5 * S, 2.2 * S);
  headGroup.add(eyeL);
  const eyeR = emissiveBox(1 * S, 1 * S, 0.5 * S, eyeGreen);
  eyeR.position.set(1.2 * S, 0.5 * S, 2.2 * S);
  headGroup.add(eyeR);

  // Mouth
  const mouth = simpleBox(3 * S, 0.5 * S, 0.5 * S, 0x0A4A4A);
  mouth.position.set(0, -1 * S, 2.2 * S);
  headGroup.add(mouth);

  headGroup.position.set(0, 2 * S, 8 * S);
  group.add(headGroup);

  // Wings: 2 flat planes on each side, slightly angled down
  const wingL = simpleBox(16 * S, 1 * S, 8 * S, wingDark);
  wingL.position.set(-18 * S, 2 * S, 0);
  wingL.rotation.z = 0.15; // angled down
  group.add(wingL);

  const wingR = simpleBox(16 * S, 1 * S, 8 * S, wingDark);
  wingR.position.set(18 * S, 2 * S, 0);
  wingR.rotation.z = -0.15; // angled down
  group.add(wingR);

  // Tail: 3 segments getting smaller, trailing behind
  const tail1 = simpleBox(8 * S, 2 * S, 6 * S, bodyTeal);
  tail1.position.set(0, 1.5 * S, -9 * S);
  group.add(tail1);

  const tail2 = simpleBox(5 * S, 1.5 * S, 5 * S, wingDark);
  tail2.position.set(0, 1.5 * S, -15 * S);
  group.add(tail2);

  const tail3 = simpleBox(3 * S, 1 * S, 4 * S, wingDark);
  tail3.position.set(0, 1.5 * S, -20 * S);
  group.add(tail3);

  // Membrane details (translucent patches on wings)
  const memL = new THREE.Mesh(
    new THREE.BoxGeometry(10 * S, 0.5 * S, 6 * S),
    new THREE.MeshLambertMaterial({ color: 0x2A8A8A, transparent: true, opacity: 0.5 })
  );
  memL.position.set(-14 * S, 2.5 * S, 0);
  group.add(memL);
  const memR = new THREE.Mesh(
    new THREE.BoxGeometry(10 * S, 0.5 * S, 6 * S),
    new THREE.MeshLambertMaterial({ color: 0x2A8A8A, transparent: true, opacity: 0.5 })
  );
  memR.position.set(14 * S, 2.5 * S, 0);
  group.add(memR);

  // No legs (flying mob)
  return { headMesh: headGroup, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ============================================================
// DROWNED MESH (Fase 5D — aquatic zombie)
// ============================================================

function buildDrowned(group) {
  const S = PX * 1.8;

  const skinMain = 0x2E7D6B;  // blue-green skin
  const skinDark = 0x1B5E4B;  // darker limbs
  const pantsCol = 0x1A4A3A;  // tattered dark pants
  const seaweed  = 0x2E8B57;  // seaweed patches

  // Torso (8w x 12h x 4d)
  const torso = simpleBox(8 * S, 12 * S, 4 * S, skinMain);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Seaweed patches on body
  const sw1 = simpleBox(2 * S, 4 * S, 0.5 * S, seaweed);
  sw1.position.set(3 * S, 20 * S, 2.3 * S);
  group.add(sw1);
  const sw2 = simpleBox(3 * S, 3 * S, 0.5 * S, seaweed);
  sw2.position.set(-2 * S, 16 * S, -2.3 * S);
  group.add(sw2);
  const sw3 = simpleBox(2 * S, 2 * S, 0.5 * S, seaweed);
  sw3.position.set(0, 22 * S, 2.3 * S);
  group.add(sw3);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, skinMain);
  headGroup.add(head);

  // Eyes (dark, sunken like zombie)
  const eyeL = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Cyan glow pupils
  const pupilL = emissiveBox(1 * S, 1 * S, 0.5 * S, 0x44DDDD);
  pupilL.position.set(-1.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilL);
  const pupilR = emissiveBox(1 * S, 1 * S, 0.5 * S, 0x44DDDD);
  pupilR.position.set(2.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilR);

  // Mouth
  const mouth = simpleBox(4 * S, 1 * S, 0.5 * S, 0x1A4A3A);
  mouth.position.set(0, -2 * S, 4.2 * S);
  headGroup.add(mouth);

  // Seaweed on head
  const headSW = simpleBox(3 * S, 3 * S, 0.5 * S, seaweed);
  headSW.position.set(-2 * S, 4 * S, 0);
  headGroup.add(headSW);

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d) — stretched forward like zombie
  const armL = new THREE.Group();
  const armLupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  const handL = simpleBox(4 * S, 2 * S, 4 * S, skinDark);
  handL.position.set(0, -13 * S, 0);
  armL.add(handL);
  armL.position.set(-6 * S, 24 * S, 0);
  armL.rotation.x = -1.2; // Arms stretched forward
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4 * S, 12 * S, 4 * S, skinDark);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);
  const handR = simpleBox(4 * S, 2 * S, 4 * S, skinDark);
  handR.position.set(0, -13 * S, 0);
  armR.add(handR);

  // Trident in right hand (50% chance)
  const hasTrident = Math.random() < 0.5;
  if (hasTrident) {
    // Trident rod
    const rod = simpleBox(2 * S, 14 * S, 2 * S, 0x6B4226);
    rod.position.set(0, -14 * S, 2 * S);
    armR.add(rod);
    // Trident tip (3 prongs)
    const tipCenter = simpleBox(2 * S, 4 * S, 2 * S, 0x3388BB);
    tipCenter.position.set(0, -22 * S, 2 * S);
    armR.add(tipCenter);
    const tipL = simpleBox(2 * S, 3 * S, 2 * S, 0x3388BB);
    tipL.position.set(-2 * S, -21 * S, 2 * S);
    armR.add(tipL);
    const tipR = simpleBox(2 * S, 3 * S, 2 * S, 0x3388BB);
    tipR.position.set(2 * S, -21 * S, 2 * S);
    armR.add(tipR);
    group.userData.hasTrident = true;
  }

  armR.position.set(6 * S, 24 * S, 0);
  armR.rotation.x = -1.2;
  group.add(armR);

  // Legs (4w x 12h x 4d)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legLmesh.position.y = -6 * S;
  legFL.add(legLmesh);
  const shoeL = simpleBox(4.2 * S, 2 * S, 4.5 * S, skinDark);
  shoeL.position.set(0, -12.5 * S, 0.2 * S);
  legFL.add(shoeL);
  legFL.position.set(-2 * S, 12 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4 * S, 12 * S, 4 * S, pantsCol);
  legRmesh.position.y = -6 * S;
  legFR.add(legRmesh);
  const shoeR = simpleBox(4.2 * S, 2 * S, 4.5 * S, skinDark);
  shoeR.position.set(0, -12.5 * S, 0.2 * S);
  legFR.add(shoeR);
  legFR.position.set(2 * S, 12 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// PILLAGER MESH (Fase 5D — hostile villager with crossbow)
// ============================================================

function buildPillager(group) {
  const S = PX * 1.8;

  const skinGray   = 0x7A7A7A;  // gray skin
  const bodyDark   = 0x3A3A3A;  // dark clothing
  const legsDark   = 0x2A2A2A;  // darker legs
  const bowBrown   = 0x6B4226;  // crossbow wood

  // Torso (8w x 12h x 4d)
  const torso = simpleBox(8 * S, 12 * S, 4 * S, bodyDark);
  torso.position.set(0, 18 * S, 0);
  group.add(torso);

  // Belt detail
  const belt = simpleBox(8.2 * S, 1.5 * S, 4.2 * S, 0x4A3A2A);
  belt.position.set(0, 13 * S, 0);
  group.add(belt);

  // Head (8x8x8) — villager-like
  const headGroup = new THREE.Group();
  const head = simpleBox(8 * S, 8 * S, 8 * S, skinGray);
  headGroup.add(head);

  // Eyes (dark, menacing)
  const eyeL = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeL.position.set(-2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2 * S, 2 * S, 0.5 * S, 0x111111);
  eyeR.position.set(2 * S, 1 * S, 4.2 * S);
  headGroup.add(eyeR);

  // Red pupils
  const pupilL = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xCC4444);
  pupilL.position.set(-1.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilL);
  const pupilR = emissiveBox(1 * S, 1 * S, 0.5 * S, 0xCC4444);
  pupilR.position.set(2.5 * S, 1 * S, 4.5 * S);
  headGroup.add(pupilR);

  // Nose (villager-style, protruding)
  const nose = simpleBox(2 * S, 3 * S, 2 * S, 0x6A6A6A);
  nose.position.set(0, -0.5 * S, 5 * S);
  headGroup.add(nose);

  // Eyebrows (angry)
  const browL = simpleBox(2.5 * S, 0.8 * S, 0.5 * S, 0x333333);
  browL.position.set(-2 * S, 3 * S, 4.2 * S);
  browL.rotation.z = 0.2;
  headGroup.add(browL);
  const browR = simpleBox(2.5 * S, 0.8 * S, 0.5 * S, 0x333333);
  browR.position.set(2 * S, 3 * S, 4.2 * S);
  browR.rotation.z = -0.2;
  headGroup.add(browR);

  // Headband (dark gray)
  const headband = simpleBox(8.4 * S, 2 * S, 8.4 * S, 0x2A2A2A);
  headband.position.set(0, 3 * S, 0);
  headGroup.add(headband);

  // 10% captain variant: red banner on head
  const isCaptain = Math.random() < 0.1;
  if (isCaptain) {
    const banner = simpleBox(2 * S, 12 * S, 1 * S, 0xCC2222);
    banner.position.set(0, 10 * S, -3 * S);
    headGroup.add(banner);
    // Banner pole
    const pole = simpleBox(0.5 * S, 14 * S, 0.5 * S, 0x5A3A1A);
    pole.position.set(0, 8 * S, -3 * S);
    headGroup.add(pole);
  }
  group.userData.isCaptain = isCaptain;

  headGroup.position.set(0, 28 * S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d)
  const armL = new THREE.Group();
  const armLupper = simpleBox(4 * S, 12 * S, 4 * S, bodyDark);
  armLupper.position.y = -6 * S;
  armL.add(armLupper);
  armL.position.set(-6 * S, 24 * S, 0);
  armL.rotation.x = -0.5;
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4 * S, 12 * S, 4 * S, bodyDark);
  armRupper.position.y = -6 * S;
  armR.add(armRupper);

  // Crossbow in right hand
  const cbBody = simpleBox(2 * S, 10 * S, 2 * S, bowBrown);
  cbBody.position.set(0, -12 * S, 2 * S);
  armR.add(cbBody);
  // Cross piece
  const cbCross = simpleBox(8 * S, 1.5 * S, 1.5 * S, bowBrown);
  cbCross.position.set(0, -8 * S, 2 * S);
  armR.add(cbCross);
  // String
  const cbString = simpleBox(0.2 * S, 0.2 * S, 6 * S, 0xCCCCCC);
  cbString.position.set(0, -8 * S, 5 * S);
  armR.add(cbString);

  armR.position.set(6 * S, 24 * S, 0);
  armR.rotation.x = -0.5;
  group.add(armR);

  // Legs (4w x 12h x 4d)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4 * S, 12 * S, 4 * S, legsDark);
  legLmesh.position.y = -6 * S;
  legFL.add(legLmesh);
  const bootL = simpleBox(4.2 * S, 2 * S, 4.5 * S, 0x1A1A1A);
  bootL.position.set(0, -12.5 * S, 0.2 * S);
  legFL.add(bootL);
  legFL.position.set(-2 * S, 12 * S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4 * S, 12 * S, 4 * S, legsDark);
  legRmesh.position.y = -6 * S;
  legFR.add(legRmesh);
  const bootR = simpleBox(4.2 * S, 2 * S, 4.5 * S, 0x1A1A1A);
  bootR.position.set(0, -12.5 * S, 0.2 * S);
  legFR.add(bootR);
  legFR.position.set(2 * S, 12 * S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// WARDEN MESH (Deep Dark boss — 500 HP, blind, detects vibrations)
// ============================================================

function buildWarden(group) {
  const S = PX * 2.5; // very large

  // Body (dark teal, massive)
  const body = simpleBox(14*S, 24*S, 10*S, 0x0C4A4E);
  body.position.y = 24*S;
  group.add(body);

  // Head (no eyes — blind mob)
  const head = simpleBox(12*S, 10*S, 10*S, 0x0A3A3E);
  head.position.y = 40*S;
  group.add(head);

  // Tendrils/antennae on head (sensing vibrations)
  for (let side = -1; side <= 1; side += 2) {
    const tendril = simpleBox(1*S, 8*S, 1*S, 0x44AAAA);
    tendril.position.set(side * 4*S, 48*S, 0);
    tendril.rotation.z = side * 0.3;
    group.add(tendril);
  }

  // Sculk patches (glowing veins on body)
  const vein1 = emissiveBox(4*S, 3*S, 1*S, 0x00CED1);
  vein1.position.set(0, 28*S, 5.5*S);
  group.add(vein1);
  const vein2 = emissiveBox(3*S, 2*S, 1*S, 0x00CED1);
  vein2.position.set(-3*S, 22*S, 5.5*S);
  group.add(vein2);

  // Mouth (dark opening)
  const mouth = simpleBox(6*S, 3*S, 2*S, 0x001515);
  mouth.position.set(0, 38*S, 5.5*S);
  group.add(mouth);

  // Arms (very long, thick)
  const armL = simpleBox(5*S, 20*S, 6*S, 0x0A3A3E);
  armL.position.set(-10*S, 22*S, 0);
  group.add(armL);
  const armR = simpleBox(5*S, 20*S, 6*S, 0x0A3A3E);
  armR.position.set(10*S, 22*S, 0);
  group.add(armR);

  // Legs (thick, short)
  const legL = simpleBox(6*S, 12*S, 6*S, 0x083030);
  legL.position.set(-4*S, 6*S, 0);
  group.add(legL);
  const legR = simpleBox(6*S, 12*S, 6*S, 0x083030);
  legR.position.set(4*S, 6*S, 0);
  group.add(legR);

  // Heart/core (glowing chest piece)
  const core = emissiveBox(4*S, 4*S, 2*S, 0x00CED1);
  core.position.set(0, 32*S, 5.5*S);
  group.add(core);

  return { headMesh: head, legFL: legL, legFR: legR, legBL: null, legBR: null, armL, armR };
}

// ============================================================
// FIREBALL ENTITY (for Ghast and Blaze)
// ============================================================

export function createFireballEntity(fromPos, toPos, size, damage) {
  const S = PX * 1.8;
  const group = new THREE.Group();

  const fbSize = (size || 1) * 3 * S;

  // Fireball core (orange emissive)
  const core = new THREE.Mesh(
    new THREE.BoxGeometry(fbSize, fbSize, fbSize),
    new THREE.MeshBasicMaterial({ color: 0xFF6600 })
  );
  group.add(core);

  // Outer glow
  const glow = new THREE.Mesh(
    new THREE.BoxGeometry(fbSize * 1.4, fbSize * 1.4, fbSize * 1.4),
    new THREE.MeshBasicMaterial({ color: 0xFF8800, transparent: true, opacity: 0.4 })
  );
  group.add(glow);

  const dir = new THREE.Vector3().subVectors(toPos, fromPos).normalize();
  group.position.copy(fromPos);

  return {
    mesh: group,
    position: fromPos.clone(),
    velocity: dir.multiplyScalar(12), // 12 blocks/s fireball speed
    damage: damage || 6,
    lifetime: 5.0,
    type: 'FIREBALL',
    explosionRadius: size >= 2 ? 2 : 1,
  };
}

// ============================================================
// MESH CREATION EXPORTS
// ============================================================

export function createZombieMesh() {
  const group = new THREE.Group();
  const refs = buildZombie(group);
  return { mesh: group, refs };
}

export function createSkeletonMesh() {
  const group = new THREE.Group();
  const refs = buildSkeleton(group);
  return { mesh: group, refs };
}

export function createCreeperMesh() {
  const group = new THREE.Group();
  const refs = buildCreeper(group);
  return { mesh: group, refs };
}

export function createSpiderMesh() {
  const group = new THREE.Group();
  const refs = buildSpider(group);
  return { mesh: group, refs };
}

export function createEndermanMesh() {
  const group = new THREE.Group();
  const refs = buildEnderman(group);
  return { mesh: group, refs };
}

export function createSlimeMesh(size) {
  const group = new THREE.Group();
  const scale = size === 'small' ? 0.5 : 1.0;
  const refs = buildSlime(group, scale);
  return { mesh: group, refs };
}

export function createZombiePiglinMesh() {
  const group = new THREE.Group();
  const refs = buildZombiePiglin(group);
  return { mesh: group, refs };
}

export function createGhastMesh() {
  const group = new THREE.Group();
  const refs = buildGhast(group);
  return { mesh: group, refs };
}

export function createBlazeMesh() {
  const group = new THREE.Group();
  const refs = buildBlaze(group);
  return { mesh: group, refs };
}

export function createWitchMesh() {
  const group = new THREE.Group();
  const refs = buildWitch(group);
  return { mesh: group, refs };
}

export function createPhantomMesh() {
  const group = new THREE.Group();
  const refs = buildPhantom(group);
  return { mesh: group, refs };
}

export function createDrownedMesh() {
  const group = new THREE.Group();
  const refs = buildDrowned(group);
  return { mesh: group, refs };
}

export function createPillagerMesh() {
  const group = new THREE.Group();
  const refs = buildPillager(group);
  return { mesh: group, refs };
}

// ============================================================
// ARROW ENTITY (for skeleton shots)
// ============================================================

export function createArrowEntity(fromPos, toPos) {
  const S = PX * 1.8;
  const group = new THREE.Group();

  // Arrow shaft
  const shaft = simpleBox(0.3 * S, 0.3 * S, 8 * S, 0x5A3A1A);
  group.add(shaft);

  // Arrow head (darker, pointy)
  const head = simpleBox(0.8 * S, 0.8 * S, 2 * S, 0x555555);
  head.position.set(0, 0, 5 * S);
  group.add(head);

  // Fletching (feather at the back)
  const fletchL = simpleBox(1.5 * S, 0.2 * S, 2 * S, 0xEEEEEE);
  fletchL.position.set(0, 0.5 * S, -3.5 * S);
  group.add(fletchL);
  const fletchR = simpleBox(0.2 * S, 1.5 * S, 2 * S, 0xEEEEEE);
  fletchR.position.set(0, 0, -3.5 * S);
  group.add(fletchR);

  // Orient the arrow toward target
  const dir = new THREE.Vector3().subVectors(toPos, fromPos).normalize();
  group.position.copy(fromPos);

  // Point the arrow along the direction
  const target = fromPos.clone().add(dir);
  group.lookAt(target);

  return {
    mesh: group,
    position: fromPos.clone(),
    velocity: dir.multiplyScalar(20), // 20 blocks/s arrow speed
    damage: HOSTILE_MOB_TYPES.SKELETON.damage,
    lifetime: 3.0, // seconds before despawning
    type: 'ARROW',
  };
}

// ============================================================
// HOSTILE AI UPDATE
// ============================================================

// Temp vectors to avoid allocations
const _tmpDir = new THREE.Vector3();
const _tmpVel = new THREE.Vector3();

/**
 * Update hostile mob AI.
 *
 * @param {object} entity - The hostile entity (must have .hostileState on it)
 * @param {THREE.Vector3} playerPos - Player world position
 * @param {number} dt - Delta time in seconds
 * @param {object} world - World object (getBlock, getTopBlock, setBlock)
 * @param {boolean} isNight - True if it's nighttime in the game
 * @param {THREE.Vector3} [playerLookDir] - Player camera forward direction (for Enderman look-aggro)
 * @returns {object|null} - Action result: { type: 'arrow', arrow } or { type: 'explode', pos, radius } or { type: 'split', pos, count } or null
 */
export function updateHostileAI(entity, playerPos, dt, world, isNight, playerLookDir) {
  const stats = HOSTILE_MOB_TYPES[entity.type];
  if (!stats) return null;

  // Initialize hostile state if not present
  if (!entity.hostileState) {
    entity.hostileState = {
      attackCooldown: 0,
      fuseTimer: 0,
      fusing: false,
      aggroed: false,
      jumpCooldown: 0,
      sunBurnTimer: 0,
      // Enderman-specific
      endermanAggroed: false,
      teleportCooldown: 0,
      waterDamageTimer: 0,
      // Slime-specific
      bounceTimer: 1.0 + Math.random() * 1.0,
      // Blaze burst
      burstRemaining: 0,
      burstCooldown: 0,
      // Zombie Piglin provoke timer
      provokeTimer: 0,
      // Phantom swoop
      swoopPhase: 'circling',
      swoopTimer: 0,
    };
    // Store initial spawn Y for floating mobs
    if (!entity.spawnY) entity.spawnY = entity.position.y;
  }

  const hs = entity.hostileState;
  const dist = entity.position.distanceTo(playerPos);

  // Store player look direction on entity for Enderman AI
  if (entity.type === 'ENDERMAN' && playerLookDir) {
    entity._playerLookDir = playerLookDir;
  }

  // ---- Sunlight burning ----
  if (stats.burnsInSunlight && !isNight) {
    // Check if exposed to sky (no block above)
    const bx = Math.floor(entity.position.x);
    const by = Math.floor(entity.position.y);
    const bz = Math.floor(entity.position.z);
    const topBlock = world.getTopBlock(bx, bz);
    if (by >= topBlock) {
      hs.sunBurnTimer += dt;
      if (hs.sunBurnTimer >= 1.0) {
        entity.health -= stats.sunDamagePerSec;
        hs.sunBurnTimer -= 1.0;

        // Visual flash: briefly tint the mesh orange/red
        if (entity.mesh) {
          _flashMesh(entity.mesh, 0xFF4400, 0.2);
        }
      }
    } else {
      hs.sunBurnTimer = 0;
    }
  }

  // ---- Enderman water damage ----
  if (entity.type === 'ENDERMAN') {
    const bx = Math.floor(entity.position.x);
    const by = Math.floor(entity.position.y);
    const bz = Math.floor(entity.position.z);
    const blockAtFeet = world.getBlock(bx, by, bz);
    if (blockAtFeet === BlockId.WATER) {
      hs.waterDamageTimer += dt;
      if (hs.waterDamageTimer >= 1.0) {
        entity.health -= stats.waterDamagePerSec;
        hs.waterDamageTimer -= 1.0;
        if (entity.mesh) {
          _flashMesh(entity.mesh, 0x4444FF, 0.2);
        }
      }
    } else {
      hs.waterDamageTimer = 0;
    }
    // Tick teleport cooldown
    if (hs.teleportCooldown > 0) {
      hs.teleportCooldown -= dt;
    }
  }

  // ---- Tick attack cooldown ----
  if (hs.attackCooldown > 0) {
    hs.attackCooldown -= dt;
  }
  if (hs.jumpCooldown > 0) {
    hs.jumpCooldown -= dt;
  }

  // ---- Check if dead ----
  if (entity.health <= 0) {
    // Slime splitting: large slimes split into 2-4 small slimes on death
    if (entity.type === 'SLIME' && entity.slimeSize !== 'small') {
      const count = 2 + Math.floor(Math.random() * 3); // 2-4
      return { type: 'split', entity, pos: entity.position.clone(), count };
    }
    return { type: 'death', entity };
  }

  // ---- Spider: neutral during day ----
  if (entity.type === 'SPIDER' && stats.hostileAtNight && !isNight) {
    // Wander passively during day
    _wanderAI(entity, dt, stats.speed * 0.5);
    return null;
  }

  // ---- Detection ----
  const inRange = dist <= stats.detectionRange;
  if (!inRange) {
    // Wander when no player nearby
    hs.aggroed = false;
    if (entity.type === 'ENDERMAN') hs.endermanAggroed = false;
    _wanderAI(entity, dt, stats.speed * 0.4);
    // Reset creeper fuse if player escapes
    if (entity.type === 'CREEPER' && hs.fusing) {
      hs.fusing = false;
      hs.fuseTimer = 0;
    }
    return null;
  }

  // Enderman: only aggros when looked at, not simply by proximity
  if (entity.type === 'ENDERMAN' && !hs.endermanAggroed) {
    hs.aggroed = false;
  } else if (entity.type === 'ZOMBIE_PIGLIN' && !entity.provoked) {
    // Zombie Piglin: neutral until attacked, handled in its AI
    hs.aggroed = false;
  } else {
    hs.aggroed = true;
  }

  // ---- Floating mobs (ghast, blaze) should not be affected by gravity ----
  if (stats.floats) {
    // Handled per-mob in their AI functions; prevent gravity from pulling them down
    // (External physics code should check entity.floats)
    entity.floats = true;
  }

  // ---- Direction to player (with A* pathfinding for ground mobs) ----
  _tmpDir.subVectors(playerPos, entity.position);
  _tmpDir.y = 0;
  const horizDist = _tmpDir.length();
  _tmpDir.normalize();

  // Use A* pathfinding for ground mobs (not floating ones like ghast/blaze)
  if (!stats.floats && hs.aggroed && horizDist > 2 && horizDist < 30) {
    // Throttle pathfinding: recalculate every 0.5s
    if (!entity._pathTimer) entity._pathTimer = 0;
    entity._pathTimer -= dt;
    if (entity._pathTimer <= 0) {
      entity._pathTimer = 0.5;
      const pathDir = getPathDirection(world, entity.position, playerPos, 80);
      if (pathDir) {
        entity._pathDir = pathDir;
      }
    }
    // Use cached path direction if available
    if (entity._pathDir) {
      _tmpDir.x = entity._pathDir.x;
      _tmpDir.z = entity._pathDir.z;
    }
  }

  // Face player
  if (entity.mesh) {
    entity.mesh.rotation.y = Math.atan2(_tmpDir.x, _tmpDir.z);
  }

  // ==================== TYPE-SPECIFIC AI ====================

  if (entity.type === 'ZOMBIE') {
    return _zombieAI(entity, hs, stats, _tmpDir, horizDist, dt, world);
  }

  if (entity.type === 'SKELETON') {
    return _skeletonAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt, world);
  }

  if (entity.type === 'CREEPER') {
    return _creeperAI(entity, hs, stats, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'SPIDER') {
    return _spiderAI(entity, hs, stats, _tmpDir, horizDist, dt, world);
  }

  if (entity.type === 'ENDERMAN') {
    return _endermanAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt, world);
  }

  if (entity.type === 'SLIME') {
    return _slimeAI(entity, hs, stats, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'ZOMBIE_PIGLIN') {
    return _zombiePiglinAI(entity, hs, stats, _tmpDir, horizDist, dt, world);
  }

  if (entity.type === 'GHAST') {
    return _ghastAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'BLAZE') {
    return _blazeAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'WITCH') {
    return _witchAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'PHANTOM') {
    return _phantomAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt);
  }

  if (entity.type === 'DROWNED') {
    return _drownedAI(entity, hs, stats, _tmpDir, horizDist, dt, world, playerPos);
  }

  if (entity.type === 'PILLAGER') {
    return _pillagerAI(entity, hs, stats, playerPos, _tmpDir, horizDist, dt, world);
  }

  return null;
}

// ==================== ZOMBIE AI ====================

function _zombieAI(entity, hs, stats, dirToPlayer, horizDist, dt, world) {
  // Walk toward player
  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  // Avoid cliffs and lava
  _avoidDanger(entity, world);

  // Jump over 1-block obstacles
  _tryJumpOverObstacle(entity, hs, dirToPlayer, world);

  // Melee attack
  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    return { type: 'melee', entity, damage: stats.damage };
  }

  return null;
}

// ==================== SKELETON AI ====================

function _skeletonAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt, world) {
  // Maintain preferred distance (8-15 blocks)
  if (horizDist < stats.preferredDistance) {
    // Back away from player
    entity.velocity.x = -dirToPlayer.x * stats.speed;
    entity.velocity.z = -dirToPlayer.z * stats.speed;
  } else if (horizDist > stats.maxDistance) {
    // Approach player
    entity.velocity.x = dirToPlayer.x * stats.speed;
    entity.velocity.z = dirToPlayer.z * stats.speed;
    _tryJumpOverObstacle(entity, hs, dirToPlayer, world);
  } else {
    // Strafe sideways
    const strafeDir = Math.random() < 0.5 ? 1 : -1;
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.5 * strafeDir;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.5 * strafeDir;
  }

  // Shoot arrow
  if (hs.attackCooldown <= 0 && horizDist <= stats.attackRange) {
    hs.attackCooldown = stats.attackCooldown;

    // Aim slightly above player center for arc
    const shootTarget = playerPos.clone();
    shootTarget.y += 1.2; // aim at chest

    const shootFrom = entity.position.clone();
    shootFrom.y += 1.5; // shoot from head height

    const arrow = createArrowEntity(shootFrom, shootTarget);
    return { type: 'arrow', arrow };
  }

  return null;
}

// ==================== CREEPER AI ====================

function _creeperAI(entity, hs, stats, dirToPlayer, horizDist, dt) {
  if (horizDist <= stats.fuseRange) {
    // Start / continue fuse
    if (!hs.fusing) {
      hs.fusing = true;
      hs.fuseTimer = 0;
    }
    hs.fuseTimer += dt;

    // Stop moving when fusing
    entity.velocity.x = 0;
    entity.velocity.z = 0;

    // Flash white effect (faster as fuse progresses)
    const flashRate = 2 + (hs.fuseTimer / stats.fuseTime) * 8;
    const flashing = Math.sin(hs.fuseTimer * flashRate * Math.PI) > 0;
    if (entity.mesh) {
      _setCreeperFlash(entity.mesh, flashing);
    }

    // Explode!
    if (hs.fuseTimer >= stats.fuseTime) {
      return {
        type: 'explode',
        pos: entity.position.clone(),
        radius: stats.explosionRadius,
        damage: stats.damage,
        entity,
      };
    }

    return null;
  }

  // Not in fuse range, approach silently
  if (hs.fusing && horizDist > stats.fuseRange + 1) {
    // Player escaped, reset fuse
    hs.fusing = false;
    hs.fuseTimer = 0;
    if (entity.mesh) {
      _setCreeperFlash(entity.mesh, false);
    }
  }

  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  return null;
}

// ==================== SPIDER AI ====================

function _spiderAI(entity, hs, stats, dirToPlayer, horizDist, dt, world) {
  // Move toward player
  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  // Jump frequently (spiders are jumpy)
  if (hs.jumpCooldown <= 0 && entity.velocity.y === 0) {
    // Jump toward player, especially when close
    if (horizDist < 6 || Math.random() < 0.15) {
      entity.velocity.y = stats.jumpForce;
      hs.jumpCooldown = 0.8 + Math.random() * 0.5;
    }
  }

  // Jump over obstacles
  _tryJumpOverObstacle(entity, hs, dirToPlayer, world);

  // Melee attack
  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    return { type: 'melee', entity, damage: stats.damage };
  }

  return null;
}

// ==================== ENDERMAN AI ====================

function _endermanAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt, world) {
  // Check if player is looking at the Enderman (dot product check)
  // The playerLookDir is passed via closure from updateHostileAI's 6th param
  if (!hs.endermanAggroed) {
    // Check if player is looking at us
    const toEntity = new THREE.Vector3().subVectors(entity.position, playerPos).normalize();
    // We need the player look direction - stored on the entity by the caller
    if (entity._playerLookDir) {
      const dot = entity._playerLookDir.dot(toEntity);
      if (dot > stats.lookAggroThreshold) {
        hs.endermanAggroed = true;
        hs.aggroed = true;
      }
    }
  }

  // If not aggroed, wander passively
  if (!hs.endermanAggroed) {
    _wanderAI(entity, dt, stats.speed * 0.4);
    return null;
  }

  // Aggroed: teleport randomly, then rush player
  if (hs.teleportCooldown <= 0 && horizDist > stats.attackRange * 2) {
    // Teleport to a random position 8-16 blocks from current location
    const tpAngle = Math.random() * Math.PI * 2;
    const tpDist = stats.teleportMinDist + Math.random() * (stats.teleportMaxDist - stats.teleportMinDist);
    const tpX = entity.position.x + Math.cos(tpAngle) * tpDist;
    const tpZ = entity.position.z + Math.sin(tpAngle) * tpDist;
    const tpY = world.getTopBlock(Math.floor(tpX), Math.floor(tpZ));

    if (tpY > 0) {
      // Check target is not water
      const targetBlock = world.getBlock(Math.floor(tpX), tpY, Math.floor(tpZ));
      if (targetBlock !== BlockId.WATER) {
        entity.position.set(tpX, tpY + 1, tpZ);
        if (entity.mesh) {
          entity.mesh.position.copy(entity.position);
        }
        hs.teleportCooldown = 2.0 + Math.random() * 1.5;
      }
    }
  }

  // Rush toward player
  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  // Jump over obstacles
  _tryJumpOverObstacle(entity, hs, dirToPlayer, world);

  // Melee attack
  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    return { type: 'melee', entity, damage: stats.damage };
  }

  return null;
}

// ==================== SLIME AI ====================

function _slimeAI(entity, hs, stats, dirToPlayer, horizDist, dt) {
  // Slimes move by bouncing
  hs.bounceTimer -= dt;
  if (hs.bounceTimer <= 0) {
    // Bounce toward player
    entity.velocity.x = dirToPlayer.x * stats.speed;
    entity.velocity.z = dirToPlayer.z * stats.speed;
    // Upward bounce
    if (entity.velocity.y === 0 || Math.abs(entity.velocity.y) < 0.5) {
      entity.velocity.y = stats.bounceForce;
    }
    hs.bounceTimer = stats.bounceInterval + (Math.random() - 0.5) * 0.5;
  } else {
    // Slow down between bounces (but don't fully stop for visual bounce)
    entity.velocity.x *= 0.95;
    entity.velocity.z *= 0.95;
  }

  // Melee attack
  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    const dmg = entity.slimeSize === 'small' ? 1 : stats.damage;
    return { type: 'melee', entity, damage: dmg };
  }

  return null;
}

// ==================== ZOMBIE PIGLIN AI ====================

function _zombiePiglinAI(entity, hs, stats, dirToPlayer, horizDist, dt, world) {
  // Neutral mob: only attacks when provoked (entity.provoked flag set externally on hit)
  if (!entity.provoked) {
    _wanderAI(entity, dt, stats.speed * 0.4);
    return null;
  }

  // Provoked: chase and attack like a zombie
  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  _tryJumpOverObstacle(entity, hs, dirToPlayer, world);

  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    return { type: 'melee', entity, damage: stats.damage };
  }

  // Un-provoke after some time (60 seconds)
  if (!hs.provokeTimer) hs.provokeTimer = 0;
  hs.provokeTimer += dt;
  if (hs.provokeTimer > 60) {
    entity.provoked = false;
    hs.provokeTimer = 0;
  }

  return null;
}

// ==================== GHAST AI ====================

function _ghastAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt) {
  // Ghast floats high and shoots fireballs
  // Maintain float height
  const targetY = entity.spawnY + stats.floatHeight;
  if (entity.position.y < targetY - 1) {
    entity.velocity.y = 2;
  } else if (entity.position.y > targetY + 1) {
    entity.velocity.y = -1;
  } else {
    entity.velocity.y = Math.sin(Date.now() * 0.001) * 0.5; // gentle bob
  }

  // Slow drift away/toward player to maintain distance
  if (horizDist < 15) {
    entity.velocity.x = -dirToPlayer.x * stats.speed * 0.5;
    entity.velocity.z = -dirToPlayer.z * stats.speed * 0.5;
  } else if (horizDist > 30) {
    entity.velocity.x = dirToPlayer.x * stats.speed * 0.5;
    entity.velocity.z = dirToPlayer.z * stats.speed * 0.5;
  } else {
    // Slow drift sideways
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.3;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.3;
  }

  // Shoot fireball
  if (hs.attackCooldown <= 0 && horizDist <= stats.attackRange) {
    hs.attackCooldown = stats.attackCooldown;

    const shootFrom = entity.position.clone();
    shootFrom.y += 0.5;

    const shootTarget = playerPos.clone();
    shootTarget.y += 1.0;

    const fireball = createFireballEntity(shootFrom, shootTarget, 2, stats.damage);
    return { type: 'fireball', fireball };
  }

  return null;
}

// ==================== BLAZE AI ====================

function _blazeAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt) {
  // Blaze hovers and shoots bursts of fireballs
  // Maintain float height
  const targetY = entity.spawnY + stats.floatHeight;
  if (entity.position.y < targetY - 1) {
    entity.velocity.y = 3;
  } else if (entity.position.y > targetY + 2) {
    entity.velocity.y = -1;
  } else {
    entity.velocity.y = Math.sin(Date.now() * 0.002) * 0.8;
  }

  // Circle around player
  if (horizDist < 8) {
    entity.velocity.x = -dirToPlayer.x * stats.speed;
    entity.velocity.z = -dirToPlayer.z * stats.speed;
  } else if (horizDist > 16) {
    entity.velocity.x = dirToPlayer.x * stats.speed;
    entity.velocity.z = dirToPlayer.z * stats.speed;
  } else {
    // Strafe
    const strafeDir = Math.sin(Date.now() * 0.001 + entity.position.x) > 0 ? 1 : -1;
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.6 * strafeDir;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.6 * strafeDir;
  }

  // Burst fire system
  if (!hs.burstRemaining) hs.burstRemaining = 0;
  if (!hs.burstCooldown) hs.burstCooldown = 0;

  // Tick burst cooldown
  if (hs.burstCooldown > 0) {
    hs.burstCooldown -= dt;
  }

  // If in middle of a burst, fire next shot
  if (hs.burstRemaining > 0 && hs.burstCooldown <= 0) {
    hs.burstRemaining--;
    hs.burstCooldown = stats.burstDelay;

    const shootFrom = entity.position.clone();
    shootFrom.y += 0.5;

    const shootTarget = playerPos.clone();
    shootTarget.y += 1.0;
    // Add slight random spread
    shootTarget.x += (Math.random() - 0.5) * 2;
    shootTarget.z += (Math.random() - 0.5) * 2;

    const fireball = createFireballEntity(shootFrom, shootTarget, 1, stats.damage);
    return { type: 'fireball', fireball };
  }

  // Start new burst
  if (hs.attackCooldown <= 0 && horizDist <= stats.attackRange && hs.burstRemaining <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    hs.burstRemaining = stats.burstCount;
    hs.burstCooldown = 0;
  }

  return null;
}

// ==================== WITCH AI (Fase 5D) ====================

const _witchPotionEffects = ['POISON', 'SLOWNESS', 'WEAKNESS'];

function _witchAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt) {
  // Maintain distance 8-12 blocks
  if (horizDist < 8) {
    // Back away from player
    entity.velocity.x = -dirToPlayer.x * stats.speed;
    entity.velocity.z = -dirToPlayer.z * stats.speed;
  } else if (horizDist > 12) {
    // Approach player
    entity.velocity.x = dirToPlayer.x * stats.speed;
    entity.velocity.z = dirToPlayer.z * stats.speed;
  } else {
    // Strafe sideways at good range
    const strafeDir = Math.sin(Date.now() * 0.001 + entity.position.x) > 0 ? 1 : -1;
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.5 * strafeDir;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.5 * strafeDir;
  }

  // Throw potion (ranged effect attack)
  if (hs.attackCooldown <= 0 && horizDist <= stats.attackRange) {
    hs.attackCooldown = stats.attackCooldown;
    const effect = _witchPotionEffects[Math.floor(Math.random() * _witchPotionEffects.length)];
    return { type: 'potion', entity, effect, targetPos: playerPos.clone() };
  }

  return null;
}

// ==================== PHANTOM AI (Fase 5D) ====================

function _phantomAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt) {
  // Initialize swoop phase
  if (!entity._swoopPhase) entity._swoopPhase = 'circling';
  if (!entity._swoopTimer) entity._swoopTimer = 0;

  const targetY = entity.spawnY + stats.floatHeight;

  if (entity._swoopPhase === 'circling') {
    // Circle high above player
    if (entity.position.y < targetY - 1) {
      entity.velocity.y = 3;
    } else if (entity.position.y > targetY + 1) {
      entity.velocity.y = -1;
    } else {
      entity.velocity.y = Math.sin(Date.now() * 0.002) * 0.5;
    }

    // Circle around player
    const circleDir = Math.sin(Date.now() * 0.0008 + entity.position.x) > 0 ? 1 : -1;
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.6 * circleDir;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.6 * circleDir;

    // Start dive when cooldown ready and player in range
    entity._swoopTimer += dt;
    if (hs.attackCooldown <= 0 && horizDist <= stats.detectionRange && entity._swoopTimer > 2.0) {
      entity._swoopPhase = 'diving';
      entity._swoopTimer = 0;
    }
  } else if (entity._swoopPhase === 'diving') {
    // Dive toward player at high speed
    const dirFull = new THREE.Vector3().subVectors(playerPos, entity.position).normalize();
    entity.velocity.x = dirFull.x * stats.speed * 1.5;
    entity.velocity.y = dirFull.y * stats.speed * 1.5;
    entity.velocity.z = dirFull.z * stats.speed * 1.5;

    // Contact damage when close
    const dist3D = entity.position.distanceTo(playerPos);
    if (dist3D < stats.attackRange) {
      hs.attackCooldown = stats.attackCooldown;
      entity._swoopPhase = 'climbing';
      entity._swoopTimer = 0;
      return { type: 'melee', entity, damage: stats.damage };
    }

    // If missed (too low or timeout), start climbing
    entity._swoopTimer += dt;
    if (entity.position.y < playerPos.y - 2 || entity._swoopTimer > 3.0) {
      entity._swoopPhase = 'climbing';
      entity._swoopTimer = 0;
    }
  } else if (entity._swoopPhase === 'climbing') {
    // Climb back up to circling altitude
    entity.velocity.y = 4;
    entity.velocity.x = dirToPlayer.x * stats.speed * 0.3;
    entity.velocity.z = dirToPlayer.z * stats.speed * 0.3;

    entity._swoopTimer += dt;
    if (entity.position.y >= targetY - 1 || entity._swoopTimer > 3.0) {
      entity._swoopPhase = 'circling';
      entity._swoopTimer = 0;
    }
  }

  return null;
}

// ==================== DROWNED AI (Fase 5D) ====================

function _drownedAI(entity, hs, stats, dirToPlayer, horizDist, dt, world, playerPos) {
  // Walk/swim toward player (works in water — no drowning check)
  entity.velocity.x = dirToPlayer.x * stats.speed;
  entity.velocity.z = dirToPlayer.z * stats.speed;

  // Jump over obstacles
  _tryJumpOverObstacle(entity, hs, dirToPlayer, world);

  // Check if this drowned has a trident (ranged attack)
  if (entity.mesh && entity.mesh.userData.hasTrident && horizDist > 3 && horizDist <= 10) {
    // Ranged trident throw (uses arrow mechanics)
    if (hs.attackCooldown <= 0) {
      hs.attackCooldown = stats.attackCooldown;

      const shootTarget = playerPos.clone();
      shootTarget.y += 1.2;
      const shootFrom = entity.position.clone();
      shootFrom.y += 1.5;

      const arrow = createArrowEntity(shootFrom, shootTarget);
      arrow.damage = stats.damage + 2; // trident does extra damage
      return { type: 'arrow', arrow };
    }
    return null;
  }

  // Melee attack
  if (horizDist <= stats.attackRange && hs.attackCooldown <= 0) {
    hs.attackCooldown = stats.attackCooldown;
    return { type: 'melee', entity, damage: stats.damage };
  }

  return null;
}

// ==================== PILLAGER AI (Fase 5D) ====================

function _pillagerAI(entity, hs, stats, playerPos, dirToPlayer, horizDist, dt, world) {
  // Maintain preferred distance 8-15 blocks (like skeleton)
  if (horizDist < stats.preferredDistance) {
    // Back away from player
    entity.velocity.x = -dirToPlayer.x * stats.speed;
    entity.velocity.z = -dirToPlayer.z * stats.speed;
  } else if (horizDist > stats.maxDistance) {
    // Approach player
    entity.velocity.x = dirToPlayer.x * stats.speed;
    entity.velocity.z = dirToPlayer.z * stats.speed;
    _tryJumpOverObstacle(entity, hs, dirToPlayer, world);
  } else {
    // Strafe sideways
    const strafeDir = Math.random() < 0.5 ? 1 : -1;
    entity.velocity.x = -dirToPlayer.z * stats.speed * 0.5 * strafeDir;
    entity.velocity.z = dirToPlayer.x * stats.speed * 0.5 * strafeDir;
  }

  // Shoot crossbow bolt (arrow)
  if (hs.attackCooldown <= 0 && horizDist <= stats.attackRange) {
    hs.attackCooldown = stats.attackCooldown;

    const shootTarget = playerPos.clone();
    shootTarget.y += 1.2;
    const shootFrom = entity.position.clone();
    shootFrom.y += 1.5;

    const arrow = createArrowEntity(shootFrom, shootTarget);
    arrow.damage = stats.damage;
    return { type: 'arrow', arrow };
  }

  return null;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Simple wander behavior for when hostile mob is idle/out of range.
 */
function _wanderAI(entity, dt, speed) {
  entity.idleTimer -= dt;
  if (entity.idleTimer > 0) {
    entity.velocity.x = 0;
    entity.velocity.z = 0;
    return;
  }

  entity.wanderTimer -= dt;
  if (entity.wanderTimer <= 0) {
    if (Math.random() < 0.3) {
      entity.idleTimer = 1 + Math.random() * 3;
      entity.velocity.x = 0;
      entity.velocity.z = 0;
    } else {
      const angle = Math.random() * Math.PI * 2;
      entity.wanderDir.set(Math.sin(angle), 0, Math.cos(angle));
      entity.wanderTimer = 1 + Math.random() * 3;
    }
  }

  entity.velocity.x = entity.wanderDir.x * speed;
  entity.velocity.z = entity.wanderDir.z * speed;

  // Avoid cliffs and lava while wandering
  if (entity._world) _avoidDanger(entity, entity._world);

  if (entity.mesh && (entity.velocity.x !== 0 || entity.velocity.z !== 0)) {
    entity.mesh.rotation.y = Math.atan2(entity.velocity.x, entity.velocity.z);
  }
}

/**
 * Check if moving forward would lead to a cliff (3+ block drop) or lava.
 * If so, stop or reverse direction.
 */
function _avoidDanger(entity, world) {
  if (!world) return;
  const vx = entity.velocity.x;
  const vz = entity.velocity.z;
  if (vx === 0 && vz === 0) return;

  // Check 1.5 blocks ahead
  const ahead = 1.5;
  const checkX = Math.floor(entity.position.x + Math.sign(vx) * ahead);
  const checkZ = Math.floor(entity.position.z + Math.sign(vz) * ahead);
  const checkY = Math.floor(entity.position.y);

  // Check for lava ahead
  const blockAhead = world.getBlock(checkX, checkY, checkZ);
  const blockBelow = world.getBlock(checkX, checkY - 1, checkZ);
  if (blockAhead === BlockId.LAVA || blockBelow === BlockId.LAVA) {
    entity.velocity.x = -vx * 0.5;
    entity.velocity.z = -vz * 0.5;
    return;
  }

  // Check for cliff (3+ block drop)
  let groundY = checkY;
  for (let dy = 0; dy < 4; dy++) {
    if (world.getBlock(checkX, checkY - dy, checkZ) !== BlockId.AIR) {
      groundY = checkY - dy;
      break;
    }
    if (dy === 3) {
      // 3+ block drop — avoid
      entity.velocity.x = -vx * 0.5;
      entity.velocity.z = -vz * 0.5;
      return;
    }
  }
}

/**
 * Try to jump over a 1-block obstacle in front of the entity.
 */
function _tryJumpOverObstacle(entity, hs, dir, world) {
  if (hs.jumpCooldown > 0 || entity.velocity.y !== 0) return;

  const checkX = Math.floor(entity.position.x + dir.x * 0.8);
  const checkY = Math.floor(entity.position.y);
  const checkZ = Math.floor(entity.position.z + dir.z * 0.8);

  const blockAhead = world.getBlock(checkX, checkY, checkZ);
  const blockAbove = world.getBlock(checkX, checkY + 1, checkZ);

  // If there's a solid block ahead at feet level but air above, jump
  if (blockAhead !== BlockId.AIR && blockAhead !== BlockId.WATER &&
      (blockAbove === BlockId.AIR || blockAbove === BlockId.WATER)) {
    entity.velocity.y = 7; // Jump force
    hs.jumpCooldown = 0.5;
  }
}

/**
 * Flash mesh white/normal for creeper fuse effect.
 */
function _setCreeperFlash(mesh, flashing) {
  mesh.traverse(child => {
    if (child.isMesh && child.material && !child.material.isMeshBasicMaterial) {
      if (flashing) {
        if (!child.userData._origColor) {
          child.userData._origColor = child.material.color.getHex();
        }
        child.material.color.setHex(0xFFFFFF);
        child.material.emissive && child.material.emissive.setHex(0x444444);
      } else {
        if (child.userData._origColor !== undefined) {
          child.material.color.setHex(child.userData._origColor);
          child.material.emissive && child.material.emissive.setHex(0x000000);
        }
      }
    }
  });
}

/**
 * Flash a mesh briefly with a given color (for sun damage indication).
 */
function _flashMesh(mesh, color, duration) {
  const origColors = [];
  mesh.traverse(child => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of mats) {
        origColors.push({ mat, color: mat.color.getHex() });
        mat.color.setHex(color);
      }
    }
  });

  setTimeout(() => {
    for (const { mat, color: orig } of origColors) {
      mat.color.setHex(orig);
    }
  }, duration * 1000);
}

/**
 * Update fireballs in flight. Call this each frame for all active fireballs.
 * @param {object} fireball - Fireball entity from createFireballEntity
 * @param {number} dt - Delta time
 * @param {THREE.Vector3} playerPos - Player position
 * @param {object} world - World object
 * @returns {object|null} - { type: 'hit', damage, pos, radius } or { type: 'stuck', pos, radius } or null
 */
export function updateFireball(fireball, dt, playerPos, world) {
  // No gravity for fireballs - they fly straight
  fireball.position.x += fireball.velocity.x * dt;
  fireball.position.y += fireball.velocity.y * dt;
  fireball.position.z += fireball.velocity.z * dt;

  if (fireball.mesh) {
    fireball.mesh.position.copy(fireball.position);
    // Spin the fireball
    fireball.mesh.rotation.y += dt * 4;
    fireball.mesh.rotation.x += dt * 2;
  }

  fireball.lifetime -= dt;
  if (fireball.lifetime <= 0) {
    return { type: 'expired' };
  }

  // Check block collision
  const bx = Math.floor(fireball.position.x);
  const by = Math.floor(fireball.position.y);
  const bz = Math.floor(fireball.position.z);
  const block = world.getBlock(bx, by, bz);
  if (block !== BlockId.AIR && block !== BlockId.WATER && block !== BlockId.LAVA &&
      block !== BlockId.NETHER_PORTAL) {
    return {
      type: 'stuck',
      pos: fireball.position.clone(),
      radius: fireball.explosionRadius,
      damage: fireball.damage,
    };
  }

  // Check player collision
  const dx = fireball.position.x - playerPos.x;
  const dy = fireball.position.y - (playerPos.y + 0.9);
  const dz = fireball.position.z - playerPos.z;
  const distSq = dx * dx + dy * dy + dz * dz;
  if (distSq < 1.2 * 1.2) {
    return {
      type: 'hit',
      damage: fireball.damage,
      pos: fireball.position.clone(),
      radius: fireball.explosionRadius,
    };
  }

  // Below world
  if (fireball.position.y < 0) {
    return { type: 'expired' };
  }

  return null;
}

/**
 * Update arrows in flight. Call this each frame for all active arrows.
 * @param {object} arrow - Arrow entity from createArrowEntity
 * @param {number} dt - Delta time
 * @param {THREE.Vector3} playerPos - Player position
 * @param {object} world - World object
 * @returns {object|null} - { type: 'hit' } if arrow hit player, { type: 'stuck' } if hit block, null if still flying
 */
export function updateArrow(arrow, dt, playerPos, world) {
  // Apply gravity to arrow
  arrow.velocity.y -= 12 * dt;

  // Move arrow
  arrow.position.x += arrow.velocity.x * dt;
  arrow.position.y += arrow.velocity.y * dt;
  arrow.position.z += arrow.velocity.z * dt;

  // Update mesh position
  if (arrow.mesh) {
    arrow.mesh.position.copy(arrow.position);
    // Re-orient along velocity
    const target = arrow.position.clone().add(arrow.velocity.clone().normalize());
    arrow.mesh.lookAt(target);
  }

  // Check lifetime
  arrow.lifetime -= dt;
  if (arrow.lifetime <= 0) {
    return { type: 'expired' };
  }

  // Check block collision
  const bx = Math.floor(arrow.position.x);
  const by = Math.floor(arrow.position.y);
  const bz = Math.floor(arrow.position.z);
  const block = world.getBlock(bx, by, bz);
  if (block !== BlockId.AIR && block !== BlockId.WATER) {
    return { type: 'stuck' };
  }

  // Check player collision (within 1 block of player position + height)
  const dx = arrow.position.x - playerPos.x;
  const dy = arrow.position.y - (playerPos.y + 0.9); // player center
  const dz = arrow.position.z - playerPos.z;
  const distSq = dx * dx + dy * dy + dz * dz;
  if (distSq < 0.8 * 0.8) { // hit radius
    return { type: 'hit', damage: arrow.damage };
  }

  // Below world
  if (arrow.position.y < 0) {
    return { type: 'expired' };
  }

  return null;
}

/**
 * Process a creeper explosion: destroy blocks in radius and return damage for player.
 * @param {THREE.Vector3} pos - Explosion center
 * @param {number} radius - Blast radius in blocks
 * @param {number} maxDamage - Maximum damage at center
 * @param {THREE.Vector3} playerPos - Player position
 * @param {object} world - World object (must have setBlock)
 * @returns {{ playerDamage: number, blocksDestroyed: number }}
 */
export function processCreeperExplosion(pos, radius, maxDamage, playerPos, world) {
  let blocksDestroyed = 0;
  const r = Math.ceil(radius);

  // Destroy blocks in sphere
  for (let dx = -r; dx <= r; dx++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dz = -r; dz <= r; dz++) {
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq > radius * radius) continue;

        const bx = Math.floor(pos.x) + dx;
        const by = Math.floor(pos.y) + dy;
        const bz = Math.floor(pos.z) + dz;

        // Don't destroy bedrock
        const block = world.getBlock(bx, by, bz);
        if (block !== BlockId.AIR && block !== BlockId.BEDROCK && block !== BlockId.WATER) {
          world.setBlock(bx, by, bz, BlockId.AIR);
          blocksDestroyed++;
        }
      }
    }
  }

  // Calculate player damage based on distance
  const playerDist = pos.distanceTo(playerPos);
  let playerDamage = 0;
  if (playerDist < radius + 2) {
    // Linear falloff: full damage at center, 0 at radius+2
    const falloff = 1 - (playerDist / (radius + 2));
    playerDamage = Math.ceil(maxDamage * Math.max(0, falloff));
  }

  return { playerDamage, blocksDestroyed };
}
